import { subjectGroups } from "@/lib/examSections";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useQuestionBank,
  useQuestionBankMeta,
  useStudentSkill,
  type QuestionFilters,
} from "@/hooks/useQuestionBank";
import { useAuth } from "@/hooks/useAuth";
import { extractGradeLevel, resolveStudentIdentity } from "@/lib/profile";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";
import QuestionCard from "./components/QuestionCard";
import FilterBar from "./components/FilterBar";

const DEFAULT_FILTERS: QuestionFilters = {
  lesson: "",
  topic: "",
  difficulty: "",
  sourceType: "",
  sourceName: "",
  year: "",
  limit: 20,
};

export default function SoruBankasi() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const identity = resolveStudentIdentity(user);
  // Öğrencinin sınıf seviyesi (ör. "5. Sınıf" -> "5").
  const gradeLevel = extractGradeLevel(identity.grade);

  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<QuestionFilters>({...DEFAULT_FILTERS, topic:searchParams.get("topic") ?? "", lesson:searchParams.get("lesson")??"", difficulty:["easy","medium","hard","very_hard"].includes(searchParams.get("difficulty")??"")?searchParams.get("difficulty")!:""});
  const { data: meta } = useQuestionBankMeta(gradeLevel);
  const { data: questions, loading, error, reload } = useQuestionBank(filters, gradeLevel);
  const { data: skill } = useStudentSkill();

  const [testCount, setTestCount] = useState(25);
  const [testDuration, setTestDuration] = useState(30);

  function updateFilters(patch: Partial<QuestionFilters>) {
    setFilters((f) => ({ ...f, ...patch }));
  }

  function startTest() {
    const query = new URLSearchParams({
      count: String(testCount),
      duration: String(testDuration),
    });
    if (filters.lesson) query.set("lesson", filters.lesson);
    if (filters.topic) query.set("topic", filters.topic);
    if (filters.difficulty) query.set("difficulty", filters.difficulty);
    if (filters.sourceType) query.set("sourceType", filters.sourceType);
    if (filters.sourceName) query.set("sourceName", filters.sourceName);
    if (filters.year) query.set("year", filters.year);
    navigate(`/ogrenci/soru-bankasi/test?${query.toString()}`);
  }

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const list = questions ?? [];

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Soru Bankası</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Önce görmediğin soruları çöz; açıklamalı çözümlerle ilerle. Havuz tamamlandığında en az gördüğün sorularla tekrar yap.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={reload} className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white">Yeni sorular getir</button>
          {identity.grade && (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-900 text-sm font-semibold whitespace-nowrap">
              <i className="ri-graduation-cap-line" aria-hidden="true"></i>
              {identity.grade} seviyesi
            </span>
          )}
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold whitespace-nowrap">
            <i className="ri-list-check-2" aria-hidden="true"></i>
            {list.length} soru
          </span>
        </div>
      </div>

      {/* Filtreler */}
      <FilterBar meta={meta} filters={filters} onChange={updateFilters} />

      {/* Adaptif zorluk önerisi */}
      {skill && (
        <div className="rounded-xl border border-accent-200 bg-accent-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500 text-background-50 shrink-0">
              <i className="ri-brain-line text-xl" aria-hidden="true"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">
                Sana özel zorluk: <span className="text-accent-700">{skill.recommendedDifficultyLabel}</span>
              </p>
              <p className="text-xs text-foreground-600 mt-0.5">
                Kaydedilen farklı sorularda doğruluk: %{skill.skillScore}. Öneri, cevaplarına ve hazır içeriklere göre güncellenir.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => updateFilters({ difficulty: skill.recommendedDifficulty })}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 whitespace-nowrap shrink-0"
          >
            <i className="ri-filter-3-line" aria-hidden="true"></i>
            Bu Zorluğu Uygula
          </button>
        </div>
      )}

      <section className="rounded-xl border border-primary-200 bg-primary-50 p-5 md:p-6">
        <div className="flex flex-col lg:flex-row gap-5 lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-primary-800 font-bold">
              <i className="ri-file-list-3-line text-xl" aria-hidden="true"></i>
              Derslere Göre Sınav Kitapçığı
            </div>
            <p className="mt-1 text-sm text-foreground-700 max-w-xl">Matematik, Türkçe ve diğer dersler ayrı bölümlerde çözülür. Sonraki ekranda her dersin soru sayısını seçebilir, hazır soruları ve toplam süreyi görebilirsin.</p>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <label className="text-xs font-semibold text-foreground-600">Ders başına üst sınır<select value={testCount} onChange={(event) => setTestCount(Number(event.target.value))} className="block mt-1 h-10 px-3 rounded-md border border-primary-200 bg-background-50 text-sm"><option value={10}>10 soru</option><option value={20}>20 soru</option><option value={25}>25 soru</option><option value={40}>40 soru</option><option value={60}>60 soru</option></select></label>
            <label className="text-xs font-semibold text-foreground-600">Süre<select value={testDuration} onChange={(event) => setTestDuration(Number(event.target.value))} className="block mt-1 h-10 px-3 rounded-md border border-primary-200 bg-background-50 text-sm"><option value={15}>15 dk</option><option value={30}>30 dk</option><option value={45}>45 dk</option><option value={60}>60 dk</option></select></label>
            <button type="button" onClick={startTest} disabled={list.length === 0} className="h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50"><i className="ri-play-fill mr-1" aria-hidden="true"></i>Bölümleri Düzenle</button>
          </div>
        </div>
      </section>

      <div className="space-y-8">{subjectGroups(list).map(([subject, rows]) => <section key={subject} className="space-y-4"><h2 className="rounded-xl bg-primary-50 p-4 text-xl font-bold">{subject} <span className="text-sm font-normal">· bu sayfada {rows.length} soru</span></h2>{rows.map(q => <QuestionCard key={q.id} question={q} />)}</section>)}</div>
      {list.length === 0 && <p className="rounded-xl border p-6">Bu seçim için yayınlanmış çalışma bulunmuyor. Başka bir konu seçebilirsin.</p>}

    </div>
  );
}
